import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Play, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { mediaUrl as M } from "@/data/pdpMedia";
import { txt, setTextOverride } from "@/data/siteText";
import { adminApi, getToken } from "@/lib/adminApi";
import { Sparkle } from "@/components/common/Decor";
import { BuyBox } from "@/components/product/BuyBox";
import { track } from "@/components/common/Analytics";
import { CATALOG } from "@/data/catalog";
import { StickyBuyBar } from "@/components/product/StickyBuyBar";
import { TrustBar } from "@/components/product/TrustBar";
import { EditorialFeatures } from "@/components/product/EditorialFeatures";
import { InBox } from "@/components/product/InBox";
import { Testimonials } from "@/components/home/Testimonials";
import { JumpNav } from "@/components/product/JumpNav";
import { PriceAnchorStrip } from "@/components/product/PriceAnchorStrip";
import { WhoItsFor, UGCVideos, AsSeenIn } from "@/components/product/PdpSections";
import { Comparison } from "@/components/home/Comparison";
import { LightModes } from "@/components/product/LightModes";
import { LightSpectrum } from "@/components/product/LightSpectrum";
import { FinalCta } from "@/components/home/Strips";
import { Transformations } from "@/components/product/ResultsProof";
import { FaqBlock } from "@/components/home/FaqBlock";
import { DayInLife } from "@/components/product/DayInLife";

const FadeMedia = ({ isVideo, src, poster, alt = "", className = "", slot, testid }) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  const cls = `${className} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`;
  return isVideo ? (
    <video src={src} poster={poster} data-media-slot={slot} data-media-type="video" data-testid={testid}
      autoPlay muted loop playsInline preload="auto" onLoadedData={() => setLoaded(true)} className={cls} />
  ) : (
    <img src={src} alt={alt} data-media-slot={slot} data-media-type="image" data-testid={testid}
      onLoad={() => setLoaded(true)} onError={() => setLoaded(false)} className={cls} />
  );
};

export default function Product() {
  const { sku } = useParams();
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [sku]);
  const BA1 = "https://customer-assets.emergentagent.com/job_leopard-luxe-glow/artifacts/drzi48lm_Aura%20Glow%20%28Instagram%20Post%20%2845%29%29.png";
  const BASE_GALLERY = [
    { id: "pdp_gallery_1", type: "image", src: M("pdp_gallery_1"), slot: "pdp_gallery_1" },
    { id: "pdp_gallery_2", type: "image", src: M("pdp_gallery_2"), slot: "pdp_gallery_2" },
    { id: "pdp_gallery_3", type: "image", src: M("pdp_gallery_3"), slot: "pdp_gallery_3" },
    { id: "pdp_transformation", type: "image", src: M("pdp_transformation"), slot: "pdp_transformation" },
    { id: "ba1", type: "image", src: BA1, ba: true, weeks: "8 weeks" },
    { id: "pdp_gallery_6", type: "image", src: M("pdp_gallery_6"), slot: "pdp_gallery_6" },
  ];

  // Custom photo order (saved live via text store; applies to all visitors)
  const [order, setOrder] = useState(() => {
    try { const s = txt("pdp_gallery_order", null); if (s) return JSON.parse(s); } catch (e) { /* noop */ }
    return BASE_GALLERY.map((g) => g.id);
  });
  useEffect(() => {
    const load = () => { try { const s = txt("pdp_gallery_order", null); if (s) setOrder(JSON.parse(s)); } catch (e) { /* noop */ } };
    const timers = [250, 900, 2000].map((t) => setTimeout(load, t));
    return () => timers.forEach(clearTimeout);
  }, []);

  // Media edit mode (reorder only available to admin while editing media)
  const isAdmin = !!getToken();
  const [editMedia, setEditMedia] = useState(() => localStorage.getItem("ag_edit_media") === "1");
  useEffect(() => {
    const check = () => setEditMedia(localStorage.getItem("ag_edit_media") === "1");
    const id = setInterval(check, 600);
    window.addEventListener("storage", check);
    return () => { clearInterval(id); window.removeEventListener("storage", check); };
  }, []);
  const canReorder = editMedia && isAdmin;

  // Meta/GA4 ViewContent on product view.
  useEffect(() => {
    const p = CATALOG.single;
    if (p) track("ViewContent", {
      content_ids: ["single"], content_type: "product", content_name: p.name,
      value: p.price, currency: "AUD",
    });
  }, []);

  const byId = Object.fromEntries(BASE_GALLERY.map((g) => [g.id, g]));
  const GALLERY = order.map((id) => byId[id]).filter(Boolean);
  BASE_GALLERY.forEach((g) => { if (!order.includes(g.id)) GALLERY.push(g); });

  const dragFrom = useRef(null);
  const persistOrder = (ids) => {
    setOrder(ids);
    setTextOverride("pdp_gallery_order", JSON.stringify(ids));
    adminApi.put("/admin/text/pdp_gallery_order", { value: JSON.stringify(ids) }).catch(() => {});
  };
  const onThumbDrop = (toIdx) => {
    const from = dragFrom.current; dragFrom.current = null;
    if (from == null || from === toIdx) return;
    const ids = GALLERY.map((g) => g.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(toIdx, 0, moved);
    persistOrder(ids);
    setActive(toIdx);
  };

  const item = GALLERY[active] || GALLERY[0];

  return (
    <div data-testid="product-page" className="bg-[#FFFCFA]">
      <section className="bg-white">
      <div className="mx-auto max-w-[1400px] px-6 pb-16 pt-8 md:px-12">
        <nav className="mb-8 text-xs uppercase tracking-[0.15em] text-[#8A7F86]">
          <Link to="/" className="hover:text-[#2C2B2C]">Home</Link> <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-[#2C2B2C]">Shop</Link> <span className="mx-2">/</span>
          <span className="text-[#2C2B2C]">Leopard Luxe™ LED Light Therapy Mask</span>
        </nav>

        <div className="relative grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          {/* Gallery */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="relative">
              <span className="blush -left-10 -top-8" style={{ width: "16rem", height: "16rem", background: "#FBC4A8", opacity: 0.16 }} aria-hidden />
              <Sparkle className="absolute -left-2 top-4 z-20 h-7 w-7 animate-twinkle text-[#F4B999]" />
              <motion.div key={active} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                className="relative z-10 overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] bg-[#F5EDE7] glow-ring">
                {item.type === "video" ? (
                  <FadeMedia isVideo src={item.src} poster={item.poster} slot={item.slot} testid="product-main-video" className="aspect-[4/3.4] w-full object-cover" />
                ) : (
                  <FadeMedia src={item.src} alt="Leopard Luxe™ LED Light Therapy Mask" slot={item.slot} testid="product-main-image" className="aspect-[4/3.4] w-full object-cover" />
                )}
                {item.ba && (
                  <>
                    <span className="absolute left-4 top-4 rounded-full bg-[#F2E8E6] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#232222] shadow-md">Before</span>
                    <span className="absolute right-4 top-4 rounded-full bg-[#F2E8E6] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#232222] shadow-md">After · {item.weeks}</span>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/30 to-transparent px-6 pb-3.5 pt-10">
                      <p data-testid="comparison-disclaimer" className="mx-auto max-w-sm text-center text-[0.66rem] font-light leading-relaxed text-white/90">
                        With consistent use, many customers notice visible improvements in skin tone, texture and overall radiance over 8 to 12 weeks. Individual results may vary.
                      </p>
                    </div>
                  </>
                )}
                {!item.ba && (
                  <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-3.5 py-1.5 shadow-md backdrop-blur">
                    <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-[#F4B999] text-[#F4B999]" />)}</div>
                    <span className="text-[0.66rem] font-medium text-[#2C2B2C]">4.9</span>
                  </div>
                )}
              </motion.div>
              <button onClick={() => setActive((active - 1 + GALLERY.length) % GALLERY.length)} data-testid="gallery-prev"
                aria-label="Previous image"
                className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#2C2B2C] shadow-[0_8px_24px_-10px_rgba(120,80,55,0.6)] backdrop-blur transition-all hover:bg-white hover:scale-105">
                <ChevronLeft className="h-5 w-5" strokeWidth={2.25} />
              </button>
              <button onClick={() => setActive((active + 1) % GALLERY.length)} data-testid="gallery-next"
                aria-label="Next image"
                className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#2C2B2C] shadow-[0_8px_24px_-10px_rgba(120,80,55,0.6)] backdrop-blur transition-all hover:bg-white hover:scale-105">
                <ChevronRight className="h-5 w-5" strokeWidth={2.25} />
              </button>
            </div>
            <div className="relative z-10 mt-4 grid grid-cols-6 gap-2.5">
              {GALLERY.map((g, i) => (
                <button key={g.id || i} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`}
                  draggable={canReorder}
                  onDragStart={canReorder ? (e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = "move"; } : undefined}
                  onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                  onDrop={canReorder ? (e) => { e.preventDefault(); onThumbDrop(i); } : undefined}
                  className={`relative aspect-square overflow-hidden rounded-2xl border-2 bg-[#F5EDE7] transition-all ${active === i ? "border-[#FBC4A8] ring-2 ring-[#F4B999]/40" : "border-[#F2E8E6] opacity-70 hover:opacity-100 hover:border-[#F4B999]"} ${canReorder ? "cursor-grab active:cursor-grabbing" : ""}`}>
                  {g.type === "video" ? (
                    <FadeMedia isVideo src={g.src} poster={g.poster} className="aspect-square w-full object-cover" />
                  ) : (
                    <FadeMedia src={g.src} className="aspect-square w-full object-cover" />
                  )}
                  {canReorder && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35">
                      <GripVertical className="h-4 w-4 text-white drop-shadow" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            {canReorder && (
              <p className="mt-2 text-center text-[0.68rem] font-medium text-[#E97C6A]" data-testid="gallery-reorder-hint">Drag photos to reorder · saves automatically</p>
            )}
          </div>

          {/* Buy box */}
          <div>
            <BuyBox />
          </div>
        </div>
      </div>
      </section>

      {/* ── Trust bar: featured in + guarantees, within first 1.5 screens ── */}
      <TrustBar />

      {/* ── Sticky jump-to nav ── */}
      <JumpNav />

      {/* ───────── WHAT IT DOES ───────── */}
      <WhoItsFor />

      {/* ───────── HOW IT WORKS (device → science) ───────── */}
      <LightModes />
      <LightSpectrum />

      {/* ── Editorial lifestyle + press authority ── */}
      <EditorialFeatures />
      <AsSeenIn />

      {/* ───────── RITUAL + WHY IT'S BETTER ───────── */}
      <DayInLife />
      <Comparison />

      {/* ───────── PROOF (results → ugc → reviews) ───────── */}
      <Transformations />
      <UGCVideos />
      <Testimonials />

      {/* ───────── VALUE + CLOSE ───────── */}
      <InBox />
      <PriceAnchorStrip />
      <FaqBlock />
      <FinalCta />

      <StickyBuyBar sku={sku === "duo" || sku === "ritual" ? sku : "single"} />
    </div>
  );
}
